import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { ArrowDown, ArrowUp, Check, Pencil, Plus, X } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { useSnackbar } from "notistack";
import { TextField } from "../ds";
import {
  createCategory,
  deactivateCategory,
  listCategories,
  reorderCategories,
  updateCategory,
} from "../api/admin";
import type { Category } from "../types/admin";

export default function AdminCategories() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: listCategories,
  });

  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState<{ id: string; label: string } | null>(
    null,
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin-categories"] });

  const addMutation = useMutation({
    mutationFn: (label: string) => createCategory({ label }),
    onSuccess: () => {
      setNewLabel("");
      enqueueSnackbar("Category added", { variant: "success" });
      invalidate();
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) =>
      updateCategory(id, { label }),
    onSuccess: () => {
      setEditing(null);
      enqueueSnackbar("Category updated", { variant: "success" });
      invalidate();
    },
  });

  const activeMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? updateCategory(id, { active: true }) : deactivateCategory(id),
    onSuccess: () => invalidate(),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderCategories(ids),
    onSuccess: () => invalidate(),
  });

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...categories];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    reorderMutation.mutate(next.map((c) => c.id));
  };

  const handleAdd = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    if (
      categories.some(
        (c) => c.label.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      enqueueSnackbar("That category already exists", { variant: "warning" });
      return;
    }
    addMutation.mutate(trimmed);
  };

  return (
    <Box>
      <Typography variant="h4" mb={0.5}>
        Categories
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Categories employees choose when submitting a suggestion. Inactive
        categories are hidden from the form but remain on historical records.
      </Typography>

      <Alert severity="info" sx={{ mb: 2, maxWidth: 720 }}>
        Changes are saved locally until the admin backend is deployed. The API
        shape mirrors the planned endpoints, so no UI rewrite will be needed.
      </Alert>

      <Card sx={{ maxWidth: 720, mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" mb={1}>
            Add a category
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Waste reduction"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={<RavenIcon icon={Plus} />}
              onClick={handleAdd}
              disabled={addMutation.isPending || !newLabel.trim()}
            >
              Add
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ maxWidth: 720 }}>
        <CardContent>
          <Typography variant="subtitle2" mb={1}>
            Current categories
          </Typography>
          {isLoading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : categories.length === 0 ? (
            <Typography color="text.secondary">
              No categories yet — add one above.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {categories.map((c: Category, idx: number) => {
                const isEditing = editing?.id === c.id;
                return (
                  <Stack
                    key={c.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      opacity: c.active ? 1 : 0.55,
                    }}
                  >
                    <Stack direction="column">
                      <IconButton
                        size="small"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                      >
                        <RavenIcon icon={ArrowUp} fontSize="inherit" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => move(idx, 1)}
                        disabled={idx === categories.length - 1}
                      >
                        <RavenIcon icon={ArrowDown} fontSize="inherit" />
                      </IconButton>
                    </Stack>
                    {isEditing ? (
                      <TextField
                        value={editing!.label}
                        onChange={(e) =>
                          setEditing({ id: c.id, label: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            if (editing!.label.trim())
                              editMutation.mutate({
                                id: c.id,
                                label: editing!.label.trim(),
                              });
                          }
                          if (e.key === "Escape") setEditing(null);
                        }}
                        autoFocus
                        sx={{ flex: 1 }}
                      />
                    ) : (
                      <Typography sx={{ flex: 1 }}>{c.label}</Typography>
                    )}
                    <Switch
                      checked={c.active}
                      onChange={(_, v) =>
                        activeMutation.mutate({ id: c.id, active: v })
                      }
                      size="small"
                    />
                    {isEditing ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (editing!.label.trim())
                              editMutation.mutate({
                                id: c.id,
                                label: editing!.label.trim(),
                              });
                          }}
                          color="primary"
                        >
                          <RavenIcon icon={Check} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setEditing(null)}
                        >
                          <RavenIcon icon={X} />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() =>
                          setEditing({ id: c.id, label: c.label })
                        }
                      >
                        <RavenIcon icon={Pencil} />
                      </IconButton>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
